/**
 * The one web path into a private chat from a person. The pool member menu's
 * `Message` row (ISSUE-1045) and `FanMessageAction` on a fan profile and a
 * member detail pane (ISSUE-1046) both use it, so every entry point runs the
 * same code.
 *
 * It opens the chat in Messages whether or not the chat still needs the other
 * fan's approval, because the first message is written there. Every failure,
 * a refused request or a server that never answered, goes through the shared
 * error modal, and the caller stays where it is.
 */
import { useChatGetOrCreatePrivate } from "@sportsmet/messaging-sdk/private";
import { useNavigate } from "@tanstack/react-router";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";

export function useOpenPrivateChat(): {
  /** Resolves true only when it navigated to the chat. */
  openPrivateChat: (targetUserId: string) => Promise<boolean>;
  isPending: boolean;
} {
  const mutation = useChatGetOrCreatePrivate();
  const navigate = useNavigate();

  const openPrivateChat = async (targetUserId: string): Promise<boolean> => {
    try {
      const result = await mutation.mutateAsync({ targetUserId });
      if (showApiError(result, "chatCreate")) return false;
      const chatId = result.data?.chatId;
      if (chatId === undefined) return false;
      void navigate({ to: "/messages", search: { conversationId: chatId } });
      return true;
    } catch {
      showOfflineError();
      return false;
    }
  };

  return { openPrivateChat, isPending: mutation.isPending };
}
